// aliange-prescribed-system.js
// Client-side state + rendering for the "Your Prescribed System" section.
// The section ships server-rendered product data in a <script type="application/json">
// tag so this file is stateless and works without any framework.

(function () {
  const sections = document.querySelectorAll('[data-section-id]');
  sections.forEach(init);

  function init(root) {
    const productsEl = root.querySelector('[data-byp-products]');
    const configEl   = root.querySelector('[data-byp-config]');
    if (!productsEl || !configEl) return;

    const PRODUCTS = JSON.parse(productsEl.textContent);
    const CONFIG   = JSON.parse(configEl.textContent);

    // Group products by step. Metafields must be set on each product:
    //   metafields.protocol.step   (e.g. "01" .. "06")
    //   metafields.protocol.phase  (e.g. "Cleanse")
    const STEPS = groupByStep(PRODUCTS);

    const state = {
      phase: 'build',              // 'build' | 'schedule'
      selected: {},                // { stepKey: productId }
      cadence: 60,
      subscribe: true,
    };

    // ─────── Render picker rows ───────
    const listEl = root.querySelector('[data-build-list]');
    STEPS.forEach(step => {
      const li = document.createElement('li');
      li.className = 'byp-row';
      li.innerHTML = `
        <div class="byp-row__head">
          <span class="byp-row__num">${step.n}</span>
          <div>
            <p class="byp-eyebrow">Step ${step.n} · ${step.phase}</p>
            <p class="byp-row__hint">${step.options.length === 1 ? 'One formulation at this step.' : `Choose one of ${step.options.length}.`}</p>
          </div>
          <span class="byp-row__status" data-step-status="${step.n}">Not added</span>
        </div>
        <div class="byp-row__opts" data-opts="${step.options.length}">
          ${step.options.map(p => `
            <button type="button" class="byp-tile" data-product-id="${p.id}" data-step="${step.n}" data-variant-id="${p.variant_id}" data-price="${p.price}">
              <div class="byp-tile__img"><img src="${p.image || ''}" alt="" loading="lazy"></div>
              <div class="byp-tile__body">
                <p class="byp-tile__name">${escapeHtml(p.title)}</p>
                <p class="byp-tile__tag">${escapeHtml(p.tagline || '')}</p>
                <p class="byp-tile__price">${formatMoney(p.price, CONFIG)}</p>
                ${p.preorder ? `<p class="byp-tile__preorder">${escapeHtml(p.preorder)}</p>` : ''}
              </div>
              <span class="byp-tile__mark">+</span>
            </button>
          `).join('')}
        </div>
      `;
      listEl.appendChild(li);
    });

    // ─────── Interaction ───────
    root.addEventListener('click', (e) => {
      const tile = e.target.closest('.byp-tile');
      if (tile) {
        const step = tile.dataset.step;
        const id   = Number(tile.dataset.productId);
        if (state.selected[step] === id) delete state.selected[step];
        else state.selected[step] = id;
        render();
        return;
      }
      const cad = e.target.closest('[data-cadence]');
      if (cad) {
        state.cadence = Number(cad.dataset.cadence);
        render();
        return;
      }
      if (e.target.closest('[data-continue]')) {
        if (state.phase === 'build') { state.phase = 'schedule'; render(); window.scrollTo({ top: root.offsetTop, behavior: 'smooth' }); }
        else { goToCheckout(); }
        return;
      }
      if (e.target.closest('[data-bar-cta]')) {
        if (state.phase === 'build') { state.phase = 'schedule'; render(); }
        else goToCheckout();
        return;
      }
      if (e.target.closest('[data-back]')) { state.phase = 'build'; render(); return; }
    });

    root.addEventListener('change', (e) => {
      if (e.target.matches('[data-subscribe]')) {
        state.subscribe = e.target.checked;
        render();
      }
    });

    // ─────── Render ───────
    function render() {
      // tile active states
      root.querySelectorAll('.byp-tile').forEach(t => {
        const step = t.dataset.step;
        const id = Number(t.dataset.productId);
        const on = state.selected[step] === id;
        t.classList.toggle('is-on', on);
        t.querySelector('.byp-tile__mark').textContent = on ? '✓' : '+';
      });

      // step status
      STEPS.forEach(s => {
        const el = root.querySelector(`[data-step-status="${s.n}"]`);
        if (!el) return;
        const on = !!state.selected[s.n];
        el.classList.toggle('is-on', on);
        el.textContent = on ? '✓ Added' : 'Not added';
      });

      // phase switch
      root.querySelector('[data-schedule]').classList.toggle('is-active', state.phase === 'schedule');
      root.querySelector('[data-build-list]').style.display = state.phase === 'build' ? '' : 'none';
      root.querySelector('[data-step-indicator="build"]').classList.toggle('is-done', state.phase === 'schedule');
      root.querySelector('[data-step-indicator="schedule"]').classList.toggle('is-active', state.phase === 'schedule');

      // cadence active
      root.querySelectorAll('[data-cadence]').forEach(c => {
        c.classList.toggle('is-on', Number(c.dataset.cadence) === state.cadence);
      });

      // summary — additive math: tier % + sub % both applied to subtotal,
      // matching the Shopify Function's per-line discount.
      const chosen = Object.values(state.selected).map(id => PRODUCTS.find(p => p.id === id)).filter(Boolean);
      const count = chosen.length;
      const subtotal = chosen.reduce((a, p) => a + Number(p.price), 0);
      const tierPct = tierFor(count);
      const subPct = (state.phase === 'schedule' && state.subscribe) ? CONFIG.subscribePct : 0;
      const tierSave = Math.round(subtotal * tierPct / 100);
      const subSave = Math.round(subtotal * subPct / 100);
      const total = subtotal - tierSave - subSave;

      // items list
      const itemsEl = root.querySelector('[data-summary-items]');
      itemsEl.innerHTML = chosen.map(p => `
        <li>
          <img src="${p.image || ''}" alt="">
          <div><p class="byp-summary__name">${escapeHtml(p.title)}</p></div>
          <span style="font-size:13px;font-variant-numeric:tabular-nums;">${formatMoney(p.price, CONFIG)}</span>
        </li>
      `).join('');

      // ladder
      const barPct = Math.min(100, (count / 5) * 100);
      root.querySelector('[data-ladder-bar]').style.width = barPct + '%';
      [3, 4, 5].forEach(n => {
        const tick = root.querySelector(`[data-tick="${n}"]`);
        if (tick) tick.classList.toggle('is-hit', count >= n);
      });
      const next = nextTier(count);
      root.querySelector('[data-ladder-note]').innerHTML = next
        ? `Add <strong>${next.need}</strong> more for <strong>${next.pct}% off</strong>.`
        : `Maximum tier reached — 20% off the complete system.`;

      // totals
      const rows = [];
      rows.push(`<div class="byp-summary__row"><span>Subtotal · ${count} ${count===1?'formulation':'formulations'}</span><span>${formatMoney(subtotal, CONFIG)}</span></div>`);
      if (tierPct > 0) rows.push(`<div class="byp-summary__row byp-summary__row--save"><span>Protocol discount · −${tierPct}%</span><span>−${formatMoney(tierSave, CONFIG)}</span></div>`);
      if (state.phase === 'schedule' && state.subscribe) rows.push(`<div class="byp-summary__row byp-summary__row--save"><span>Subscribe & save · −10%</span><span>−${formatMoney(subSave, CONFIG)}</span></div>`);
      const cadenceLabel = cadenceLabelFor(state.cadence);
      rows.push(`<div class="byp-summary__row byp-summary__row--total"><span>Total${state.phase==='schedule'&&state.subscribe?' · '+cadenceLabel:''}</span><span>${formatMoney(total, CONFIG)}</span></div>`);
      root.querySelector('[data-totals]').innerHTML = rows.join('');

      // CTA
      const btn = root.querySelector('[data-continue]');
      btn.disabled = count === 0;
      if (state.phase === 'build') btn.textContent = count === 0 ? 'Add formulations to continue' : 'Continue to schedule →';
      else btn.textContent = state.subscribe ? `Subscribe · ${formatMoney(total, CONFIG)} ${cadenceLabel}` : `Buy once · ${formatMoney(total, CONFIG)}`;

      // mobile bar
      root.querySelector('[data-bar-count]').textContent = `${count} ${count===1?'formulation':'formulations'}`;
      root.querySelector('[data-bar-total]').textContent = formatMoney(total, CONFIG);
      root.querySelector('[data-bar-cta]').disabled = count === 0;
      root.querySelector('[data-bar-cta]').textContent = state.phase === 'build' ? 'Continue →' : 'Checkout →';
    }

    // ─────── Checkout ───────
    // Posts directly to /cart/add with selling_plan if subscribing, then redirects.
    async function goToCheckout() {
      const chosen = Object.values(state.selected).map(id => PRODUCTS.find(p => p.id === id)).filter(Boolean);
      if (!chosen.length) return;

      const selling_plan = state.subscribe ? CONFIG.sellingPlans[String(state.cadence)] : null;
      const items = chosen.map(p => ({
        id: p.variant_id,
        quantity: 1,
        ...(selling_plan ? { selling_plan: Number(selling_plan) } : {}),
        properties: { _protocol_bundle: 'prescribed-system' }
      }));

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items })
        });
        if (!res.ok) throw new Error(await res.text());
        // Protocol tier discount is enforced by a Shopify Function /
        // automatic discount keyed off the _protocol_bundle cart attribute
        // or line-item property. See HANDOFF.md for the discount spec.
        window.location.href = '/checkout';
      } catch (err) {
        console.error('[prescribed-system] add-to-cart failed', err);
        alert('Something went wrong adding your protocol to cart. Please try again.');
      }
    }

    // ─────── Helpers ───────
    function groupByStep(products) {
      const map = {};
      products.forEach(p => {
        const n = p.step || '00';
        if (!map[n]) map[n] = { n, phase: p.phase || '', options: [] };
        map[n].options.push(p);
      });
      return Object.values(map).sort((a, b) => a.n.localeCompare(b.n));
    }
    function tierFor(count) {
      if (count >= 5) return 20;
      if (count === 4) return 15;
      if (count === 3) return 10;
      return 0;
    }
    function nextTier(count) {
      if (count < 3) return { need: 3 - count, pct: 10 };
      if (count < 4) return { need: 1, pct: 15 };
      if (count < 5) return { need: 1, pct: 20 };
      return null;
    }
    function cadenceLabelFor(d) {
      if (d === 30) return 'monthly';
      if (d === 60) return 'every 2 months';
      if (d === 90) return 'quarterly';
      return `every ${d} days`;
    }
    function formatMoney(cents, cfg) {
      // Shopify prices come in cents. Fallback to basic $X formatting.
      const n = (Number(cents) / 100).toFixed(0);
      return '$' + n;
    }
    function escapeHtml(s) {
      return String(s || '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
    }

    render();
  }
})();
